import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { QUEUE_ACCESS } from '@gdec/shared';
import { useAuth } from '@/auth/AuthContext';
import { AppShell } from '@/components/AppShell';
import { RequireAccess } from '@/components/RequireAccess';
import { LoadingBlock } from '@/components/Spinner';

import { EmployeeFinancePage } from '@/pages/EmployeeFinancePage';
import { ExchangeRatesPage } from '@/pages/ExchangeRatesPage';
import { AccessManagementPage } from '@/pages/AccessManagementPage';
import { AdminRequestsPage } from '@/pages/AdminRequestsPage';
import { ApprovalsPage } from '@/pages/ApprovalsPage';
import { AuditTrailPage } from '@/pages/AuditTrailPage';
import { ClaimFormPage } from '@/pages/ClaimFormPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { FinanceReviewPage } from '@/pages/FinanceReviewPage';
import { FinancialDashboardPage } from '@/pages/FinancialDashboardPage';
import { LoginPage } from '@/pages/LoginPage';
import { AfterMicrosoftSignIn, MicrosoftCallbackPage } from '@/pages/MicrosoftCallbackPage';
import { MyRequestsPage } from '@/pages/MyRequestsPage';
import { NotFiledPage } from '@/pages/NotFiledPage';
import { OrgChartPage } from '@/pages/OrgChartPage';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { ProfileSetupPage } from '@/pages/ProfileSetupPage';

export function App(): ReactNode {
  const { user, loading, mustChangePassword } = useAuth();

  if (loading && !user) {
    return <LoadingBlock label="Restoring your session…" />;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="auth/microsoft/callback" element={<MicrosoftCallbackPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  // A password set by an administrator has been seen by whoever set it, so it is
  // a way in rather than a credential. Nothing else is reachable until it is
  // replaced.
  if (mustChangePassword) {
    return (
      <Routes>
        <Route element={<AppShell />}>
          <Route path="*" element={<ChangePasswordPage forced />} />
        </Route>
      </Routes>
    );
  }

  // First login: nothing else is reachable until the profile is confirmed,
  // because every claim form is populated from these fields.
  if (!user.profileComplete) {
    return (
      <Routes>
        <Route path="*" element={<ProfileSetupPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="claim-new" element={<ClaimFormPage />} />
        <Route path="claim-new/:claimId" element={<ClaimFormPage />} />
        <Route path="my-requests" element={<MyRequestsPage />} />
        <Route
          path="approvals"
          element={
            <RequireAccess allow={QUEUE_ACCESS.approvals}>
              <ApprovalsPage />
            </RequireAccess>
          }
        />
        <Route
          path="finance-review"
          element={
            <RequireAccess allow={QUEUE_ACCESS.financeReview}>
              <FinanceReviewPage />
            </RequireAccess>
          }
        />
        <Route
          path="financial-dashboard"
          element={
            <RequireAccess allow={QUEUE_ACCESS.financialDashboard}>
              <FinancialDashboardPage />
            </RequireAccess>
          }
        />
        <Route
          path="org-chart"
          element={
            <RequireAccess allow={QUEUE_ACCESS.admin}>
              <OrgChartPage />
            </RequireAccess>
          }
        />
        <Route
          path="exchange-rates"
          element={
            <RequireAccess allow={QUEUE_ACCESS.exchangeRates}>
              <ExchangeRatesPage />
            </RequireAccess>
          }
        />
        <Route
          path="not-filed"
          element={
            <RequireAccess allow={QUEUE_ACCESS.financeReview}>
              <NotFiledPage />
            </RequireAccess>
          }
        />
        <Route
          path="employee-finance"
          element={
            <RequireAccess allow={QUEUE_ACCESS.employeeFinanceFields}>
              <EmployeeFinancePage />
            </RequireAccess>
          }
        />
        <Route
          path="access-mgmt"
          element={
            <RequireAccess allow={QUEUE_ACCESS.admin}>
              <AccessManagementPage />
            </RequireAccess>
          }
        />
        <Route
          path="all-requests"
          element={
            <RequireAccess allow={QUEUE_ACCESS.admin}>
              <AdminRequestsPage />
            </RequireAccess>
          }
        />
        <Route
          path="audit-trail"
          element={
            <RequireAccess allow={QUEUE_ACCESS.admin}>
              <AuditTrailPage />
            </RequireAccess>
          }
        />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="change-password" element={<ChangePasswordPage />} />
        {/* Signed in and at the callback path: either the sign-in just
            completed (go where it started from) or a stale tab (dashboard). */}
        <Route path="auth/microsoft/callback" element={<AfterMicrosoftSignIn />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}
